import { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import WatchlistDashboardService, { CustomWatchlistCardData } from '../WatchlistDashboardService';
import CustomWatchlistCard from './CustomWatchlistCard';
import CreateWatchlistModal from './CreateWatchlistModal';

/** Custom Watchlists section: existing cards + a "Create" card that opens the modal. */
const CustomWatchlistSection = ({ instituteId, filterInstituteId }: { instituteId: string | number; filterInstituteId?: string | null }) => {
    const [cards, setCards] = useState<CustomWatchlistCardData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState<CustomWatchlistCardData | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        WatchlistDashboardService.getCustomWatchlists(instituteId, filterInstituteId)
            .then((r) => setCards(r.watchlists))
            .catch((err) => setError(err?.message || 'Failed to load custom watchlists.'))
            .finally(() => setLoading(false));
    }, [instituteId, filterInstituteId]);

    const handleDelete = useCallback(async (card: CustomWatchlistCardData) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `This will permanently delete the watchlist "${card.name}".`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await WatchlistDashboardService.deleteCustomWatchlist(instituteId, card.id, filterInstituteId);
            setCards((prev) => prev.filter((c) => c.id !== card.id));
            Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'Watchlist deleted successfully.',
                showConfirmButton: false,
                timer: 1500,
                timerProgressBar: true,
            });
        } catch (err: any) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err?.message || 'Failed to delete watchlist. Please try again.',
                confirmButtonText: 'Close',
            });
        }
    }, [instituteId, filterInstituteId]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="col-12">
            <div className="d-flex align-items-center gap-3 mt-4">
                <h3 className="font-18 font-600 text-dark-blue m-0">Custom Watchlists</h3>
                <p className="m-0 font-12 text-secondary-emphasis">User-defined tracking — add to any layer as Signal or Strategic context</p>
            </div>

            {loading && <div className="font-14 text-secondary-emphasis py-2">Loading custom watchlists…</div>}
            {error && !loading && <div className="alert alert-danger font-13 mt-2">{error}</div>}

            {!loading && !error && (
                <div className="row g-3 row-cols-1 row-cols-md-2 mb-4 mt-1">
                    {cards.map((card) => (
                        <CustomWatchlistCard
                            key={card.id}
                            card={card}
                            instituteId={instituteId}
                            filterInstituteId={filterInstituteId}
                            onEdit={(c) => { setEditing(c); setShowModal(true); }}
                            onDelete={handleDelete}
                        />
                    ))}

                    {/* Create card */}
                    <div className="col">
                        <div
                            className="bg-white border rounded-4 p-4 h-100 d-flex flex-column align-items-center justify-content-center text-center cursor-pointer"
                            style={{ border: '2px dashed #cbd5e1', minHeight: 180 }}
                            onClick={() => { setEditing(null); setShowModal(true); }}>
                            <div className="d-flex align-items-center justify-content-center rounded-circle mb-3 bg-primary bg-opacity-10 text-primary font-22" style={{ width: 50, height: 50 }}>＋</div>
                            <div className="font-18 font-600 text-dark-blue mb-2">Create Custom Watchlist</div>
                            <div className="font-14 text-secondary">Define your own criteria — tag as Signal or Strategic. Add alumni by search.</div>
                        </div>
                    </div>
                </div>
            )}

            {showModal && (
                <CreateWatchlistModal
                    instituteId={instituteId}
                    filterInstituteId={filterInstituteId}
                    editWatchlist={editing || undefined}
                    onClose={() => { setShowModal(false); setEditing(null); }}
                    onCreated={() => {
                        setShowModal(false);
                        setEditing(null);
                        load();
                    }}
                />
            )}
        </div>
    );
};

export default CustomWatchlistSection;
